<template>
  <div :class="wrapperClass">
    <div class="bg-white p-6 sm:p-8">

      <div v-if="title || $slots['header-actions']" class="mb-6 flex items-center justify-between">
        <h2 v-if="title" class="text-xl font-bold text-gray-900">{{ title }}</h2>
        <div><slot name="header-actions" /></div>
      </div>

      <form @submit.prevent="onSubmit" class="space-y-10">

        <template v-for="(section, sIdx) in sections" :key="sIdx">
          <div v-if="isVisible(section.visibleIf)" class="space-y-5 max-w-3xl mx-auto">

            <h3 v-if="section.title" class="text-lg font-bold text-gray-900">
              {{ section.title }}
            </h3>

            <div :class="['grid gap-4', section.cols === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1']">

              <template v-for="(f, i) in section.fields" :key="`${sIdx}-${i}-${f.key}`">
                <div v-if="isVisible(f.visibleIf)" :class="f.colSpan === 2 ? 'md:col-span-2' : ''">

                  <label class="block text-md font-medium text-gray-800 mb-1">
                    {{ f.label }}
                    <!-- <span v-if="f.required" class="text-red-500 ml-0.5">*</span> -->
                  </label>

                  <!-- TEXT / NUMBER -->
                  <input
                    v-if="['text','number'].includes(f.type)"
                    :type="f.type"
                    :ref="`field_${f.key}`"
                    :placeholder="f.placeholder"
                    :tabindex="f.tabIndex"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm transition-colors"
                    :class="errors[f.key] ? 'border-red-400 focus:border-red-500 outline-none' : 'border-gray-300 focus:border-blue-500 outline-none'"
                    :disabled="isDisabled(f.disabledIf) || isView"
                    v-model="localModel[f.key]"
                    @input="clearError(f.key)"
                  />

                  <!-- DATE -->
                  <input
                    v-if="['date', 'month'].includes(f.type)"
                    :type="f.type"
                    :ref="`field_${f.key}`"
                    :tabindex="f.tabIndex"
                    class="w-full h-10 rounded-xl border-2 px-3 text-sm transition-colors outline-none"
                    :class="errors[f.key] ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'"
                    :disabled="isDisabled(f.disabledIf) || isView"
                    v-model="localModel[f.key]"
                    @change="clearError(f.key)"
                  />

                  <!-- SELECT (NATIVE & CUSTOM) -->
                  <div v-else-if="f.type==='select'" class="relative" :ref="`field_${f.key}`">
                    <!-- Native Select Option -->
                    <select
                      v-if="f.native"
                      v-model="localModel[f.key]"
                      :tabindex="f.tabIndex"
                      :disabled="isDisabled(f.disabledIf) || isView"
                      class="w-full h-10 rounded-xl border-2 px-3 text-sm bg-white transition-colors appearance-none outline-none"
                      :class="errors[f.key] ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'"
                      @change="clearError(f.key)"
                    >
                      <option value="" disabled selected>{{ f.placeholder || 'Select option' }}</option>
                      <option v-for="opt in f.options" :key="opt.value ?? opt" :value="opt.value ?? opt">
                        {{ opt.label ?? opt }}
                      </option>
                    </select>

                    <!-- Custom Searchable Select Element -->
                    <div
                      v-else
                      :tabindex="(isDisabled(f.disabledIf) || isView) ? -1 : (f.tabIndex || 0)"
                      class="w-full h-10 rounded-xl border-2 px-3 text-sm flex items-center justify-between bg-white transition-colors focus:border-blue-500 outline-none"
                      :class="[
                        errors[f.key] ? 'border-red-400' : 'border-gray-300',
                        (isDisabled(f.disabledIf) || isView) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                      ]"
                      @click="(!isDisabled(f.disabledIf) && !isView) && toggleDropdown(f.key)"
                      @keydown.enter.prevent="(!isDisabled(f.disabledIf) && !isView) && toggleDropdown(f.key)"
                      @keydown.space.prevent="(!isDisabled(f.disabledIf) && !isView) && toggleDropdown(f.key)"
                    >
                      <span :class="!localModel[f.key] ? 'text-gray-400' : ''">
                        {{ getLabel(f, localModel[f.key]) || f.placeholder || 'Select' }}
                      </span>
                      <span class="text-gray-400 text-xs">▼</span>
                    </div>

                    <!-- Dropdown Options Panel -->
                    <div
                      v-if="openDropdown === f.key"
                      class="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg z-50"
                    >
                      <input
                        v-model="search[f.key]"
                        type="text"
                        placeholder="Search..."
                        class="w-full px-3 py-2 border-b text-sm outline-none"
                        @keydown="handleSelectKeydown(f, $event)"
                        @input="activeOptionIndex = 0"
                        ref="dropdownSearchInput"
                      />
                      <div class="max-h-48 overflow-y-auto" ref="optionsContainer">
                        <div
                          v-for="(opt, oIdx) in filteredOptions(f)"
                          :key="opt.value ?? opt"
                          @click="selectOption(f, opt)"
                          @mouseenter="activeOptionIndex = oIdx"
                          class="px-3 py-2 text-sm cursor-pointer transition-colors"
                          :class="[
                            activeOptionIndex === oIdx ? 'bg-gray-100 font-medium' : '',
                            (localModel[f.key] === (opt.value ?? opt)) ? 'text-blue-600 bg-blue-50/30' : 'text-gray-700'
                          ]"
                        >
                          {{ opt.label ?? opt }}
                        </div>
                        <div v-if="filteredOptions(f).length === 0" class="px-3 py-3 text-sm text-center text-gray-400">
                          No options found
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- COMBOBOX — dropdown list + free-text input -->
                  <div v-else-if="f.type==='combobox'" class="relative" :ref="`field_${f.key}`">
                    <div class="relative">
                      <input
                        type="text"
                        :placeholder="f.placeholder"
                        :tabindex="(isDisabled(f.disabledIf) || isView) ? -1 : (f.tabIndex || 0)"
                        class="w-full h-10 rounded-xl border-2 px-3 pr-8 text-sm transition-colors"
                        :class="[
                          errors[f.key] ? 'border-red-400 focus:border-red-500 outline-none' : 'border-gray-300 focus:border-blue-500 outline-none',
                          (isDisabled(f.disabledIf) || isView) ? 'cursor-not-allowed opacity-60 bg-gray-50' : 'bg-white'
                        ]"
                        :disabled="isDisabled(f.disabledIf) || isView"
                        v-model="localModel[f.key]"
                        @input="onComboboxInput(f); clearError(f.key)"
                        @focus="onComboboxFocus(f)"
                        @blur="onComboboxBlur(f)"
                        @keydown="handleComboboxKeydown(f, $event)"
                      />
                      <!-- Arrow icon -->
                      <span
                        v-if="!isDisabled(f.disabledIf) && !isView"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none select-none"
                      >▼</span>
                    </div>

                    <!-- Dropdown Options Panel -->
                    <div
                      v-if="openDropdown === f.key && comboboxOptions(f).length > 0"
                      class="absolute left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto"
                      ref="comboOptionsContainer"
                    >
                      <div
                        v-for="(opt, oIdx) in comboboxOptions(f)"
                        :key="opt.value ?? opt"
                        @mousedown.prevent="selectComboOption(f, opt)"
                        @mouseenter="activeOptionIndex = oIdx"
                        class="px-3 py-2 text-sm cursor-pointer transition-colors"
                        :class="[
                          activeOptionIndex === oIdx ? 'bg-gray-100 font-medium' : '',
                          (localModel[f.key] === (opt.value ?? opt)) ? 'text-blue-600 bg-blue-50/30' : 'text-gray-700'
                        ]"
                      >
                        {{ opt.label ?? opt }}
                      </div>
                    </div>
                  </div>

                  <!-- TEXTAREA -->
                  <textarea
                    v-else-if="f.type==='textarea'"
                    rows="4"
                    :ref="`field_${f.key}`"
                    :placeholder="f.placeholder"
                    :tabindex="f.tabIndex"
                    class="w-full rounded-xl border-2 px-3 py-2 text-sm transition-colors"
                    :class="errors[f.key] ? 'border-red-400 focus:border-red-500 outline-none' : 'border-gray-300 focus:border-blue-500 outline-none'"
                    :disabled="isDisabled(f.disabledIf) || isView"
                    v-model="localModel[f.key]"
                    @input="clearError(f.key)"
                  />

                  <!-- SLOT -->
                  <slot
                    v-else
                    :name="`field:${f.key}`"
                    :model="localModel"
                    :field="f"
                  />

                  <!-- ERROR MESSAGE -->
                  <p v-if="errors[f.key]" class="mt-1 text-xs text-red-500">
                    {{ errors[f.key] }}
                  </p>

                </div>
              </template>

            </div>
          </div>
        </template>

      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DynamicForm',

  props: {
    title: String,
    wrapperClass: String,
    schema: { type: Object, required: true },
    modelValue: { type: Object, default: () => ({}) },
    mode: { type: String, default: 'add' },
    errors: { type: Object, default: () => ({}) },
  },

  emits: ['submit', 'update:modelValue', 'clearError'],

  data() {
    return {
      localModel: { ...this.modelValue },
      syncing: false,
      openDropdown: null,
      search: {},
      activeOptionIndex: 0,
    };
  },

  computed: {
    isView() {
      return this.mode === 'view';
    },
    sections() {
      return this.schema?.sections || [];
    },
  },

  watch: {
    modelValue: {
      deep: true,
      handler(v) {
        this.syncing = true;
        this.localModel = { ...v };
        this.$nextTick(() => (this.syncing = false));
      },
    },
    localModel: {
      deep: true,
      handler(v) {
        if (this.syncing) return;
        this.$emit('update:modelValue', v);
      },
    },
  },

  methods: {
    isVisible(rule) {
      if (!rule) return true;
      if (typeof rule === 'function') return !!rule(this.localModel);
      return true;
    },

    isDisabled(rule) {
      if (!rule) return false;
      if (typeof rule === 'function') return !!rule(this.localModel);
      return !!rule;
    },

    onSubmit() {
      this.$emit('submit', { ...this.localModel });
    },

    clearError(key) {
      this.$emit('clearError', key);
    },

    // ─── SELECT (custom dropdown) ─────────────────────────────────────────────

    toggleDropdown(key) {
      this.openDropdown = this.openDropdown === key ? null : key;
      if (this.openDropdown) {
        this.clearError(key);
        this.activeOptionIndex = 0;
        this.$nextTick(() => {
          if (this.$refs.dropdownSearchInput && this.$refs.dropdownSearchInput[0]) {
            this.$refs.dropdownSearchInput[0].focus();
          }
        });
      }
    },

    selectOption(field, opt) {
      this.localModel[field.key] = opt.value ?? opt;
      this.openDropdown = null;
      this.clearError(field.key);
    },

    getLabel(field, value) {
      const found = (field.options || []).find(o => (o.value ?? o) === value);
      return found ? (found.label ?? found) : '';
    },

    filteredOptions(field) {
      const term = (this.search[field.key] || '').toLowerCase();
      return (field.options || []).filter(o =>
        (o.label ?? o).toLowerCase().includes(term)
      );
    },

    handleSelectKeydown(field, event) {
      const options = this.filteredOptions(field);
      if (options.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeOptionIndex = (this.activeOptionIndex + 1) % options.length;
        this.scrollActiveOptionIntoView();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeOptionIndex = (this.activeOptionIndex - 1 + options.length) % options.length;
        this.scrollActiveOptionIntoView();
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const selectedOpt = options[this.activeOptionIndex];
        if (selectedOpt) {
          this.selectOption(field, selectedOpt);
        }
      } else if (event.key === 'Escape') {
        this.openDropdown = null;
      }
    },

    scrollActiveOptionIntoView() {
      this.$nextTick(() => {
        const container = this.$refs.optionsContainer ? this.$refs.optionsContainer[0] : null;
        if (!container) return;
        const activeEl = container.children[this.activeOptionIndex];
        if (!activeEl) return;

        const containerTop    = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elemTop         = activeEl.offsetTop;
        const elemBottom      = elemTop + activeEl.clientHeight;

        if (elemTop < containerTop) {
          container.scrollTop = elemTop;
        } else if (elemBottom > containerBottom) {
          container.scrollTop = elemBottom - container.clientHeight;
        }
      });
    },

    // ─── COMBOBOX ─────────────────────────────────────────────────────────────

    // الخيارات المفلترة بناءً على النص المكتوب
    comboboxOptions(field) {
      const term = (this.localModel[field.key] || '').toLowerCase();
      const opts = field.options || [];
      // إذا النص فاضي أو مش موجود بالخيارات — اعرض كل الخيارات
      // إذا في نص — فلتر
      if (!term) return opts;
      return opts.filter(o => (o.label ?? o).toLowerCase().includes(term));
    },

    onComboboxInput(field) {
      // فتح الدروبداون عند الكتابة
      this.openDropdown = field.key;
      this.activeOptionIndex = 0;
    },

    onComboboxFocus(field) {
      if (this.openDropdown !== field.key) {
        this.openDropdown = field.key;
        this.activeOptionIndex = 0;
      }
    },

    onComboboxBlur(field) {
      // تأخير بسيط عشان mousedown على الخيار يشتغل قبل blur يغلق الدروبداون
      setTimeout(() => {
        if (this.openDropdown === field.key) {
          this.openDropdown = null;
        }
      }, 150);
    },

    selectComboOption(field, opt) {
      this.localModel[field.key] = String(opt.value ?? opt);
      this.openDropdown = null;
      this.clearError(field.key);
    },

    handleComboboxKeydown(field, event) {
      const options = this.comboboxOptions(field);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.openDropdown = field.key;
        this.activeOptionIndex = Math.min(this.activeOptionIndex + 1, options.length - 1);
        this.scrollComboOptionIntoView();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeOptionIndex = Math.max(this.activeOptionIndex - 1, 0);
        this.scrollComboOptionIntoView();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const selected = options[this.activeOptionIndex];
        if (selected) {
          this.selectComboOption(field, selected);
        } else {
          // مكتوب يدوي — أغلق الدروبداون واحتفظ بالقيمة
          this.openDropdown = null;
        }
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        this.openDropdown = null;
      }
    },

    scrollComboOptionIntoView() {
      this.$nextTick(() => {
        const container = this.$refs.comboOptionsContainer
          ? (Array.isArray(this.$refs.comboOptionsContainer)
              ? this.$refs.comboOptionsContainer[0]
              : this.$refs.comboOptionsContainer)
          : null;
        if (!container) return;
        const activeEl = container.children[this.activeOptionIndex];
        if (!activeEl) return;

        const containerTop    = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elemTop         = activeEl.offsetTop;
        const elemBottom      = elemTop + activeEl.clientHeight;

        if (elemTop < containerTop) {
          container.scrollTop = elemTop;
        } else if (elemBottom > containerBottom) {
          container.scrollTop = elemBottom - container.clientHeight;
        }
      });
    },
  },
};
</script>
